import * as React from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import {
  fetchChannelSummaryByDate,
  fetchClientSummaryByDate,
  fetchDailyPulseByDMA,
  fetchDailyPulseByState,
  fetchVerticalSummary,
} from '@src/api';
import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const DAY_RANGE_OPTIONS = [7, 14, 30];

const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    bgcolor: 'background.neutral',
  },
};

const stickyControlsSx = {
  position: 'sticky',
  top: 72,
  zIndex: 12,
  borderRadius: 14,
  boxShadow: '0 18px 32px rgba(15, 23, 42, 0.12)',
  border: '1px solid',
  borderColor: 'divider',
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getTrailingMonthRange = (anchorDate, monthCount = 9) => {
  const safeAnchor = anchorDate instanceof Date && !Number.isNaN(anchorDate.getTime()) ? anchorDate : new Date();
  const end = new Date(safeAnchor.getFullYear(), safeAnchor.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  const start = new Date(end.getFullYear(), end.getMonth() - (monthCount - 1), 1);
  start.setHours(0, 0, 0, 0);
  return { start, end };
};

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  if (!denom) return 0;
  return num / denom;
};

function aggregateBreakdownRows(
  summaryRows = [],
  clientMeta = [],
  selectedClientId = '',
  getLabel = () => 'Unspecified',
  labelKey = 'label'
) {
  const clientFeeById = new Map(
    (clientMeta || []).map((client = {}) => [String(client.value), safeNumber(client.feeRate)])
  );
  const buckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }
    const label = getLabel(row);

    if (!buckets.has(label)) {
      buckets.set(label, {
        id: label,
        [labelKey]: label,
        feeWeightedSpend: 0,
        clientCountSet: new Set(),
        precomputedClientCount: 0,
        total_spend_sum: 0,
        total_leads_sum: 0,
        self_books_sum: 0,
        accepted_sum: 0,
        success_sum: 0,
        out_of_areas_count_sum: 0,
        booked_sum: 0,
        issues_sum: 0,
        scheduled_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
      });
    }

    const bucket = buckets.get(label);
    const spend = safeNumber(row.total_spend_sum);
    const feeRate = clientId ? clientFeeById.get(clientId) || 0 : 0;
    bucket.precomputedClientCount += safeNumber(row.client_count);
    bucket.total_spend_sum += spend;
    bucket.feeWeightedSpend += spend * (1 + feeRate);
    if (spend > 0 && clientId) {
      bucket.clientCountSet.add(clientId);
    }
    bucket.total_leads_sum += safeNumber(row.total_leads_sum);
    bucket.self_books_sum += safeNumber(row.self_books_sum);
    bucket.accepted_sum += safeNumber(row.accepted_sum);
    bucket.success_sum += safeNumber(row.success_sum);
    bucket.out_of_areas_count_sum += safeNumber(row.out_of_areas_count_sum);
    bucket.booked_sum += safeNumber(row.booked_sum);
    bucket.issues_sum += safeNumber(row.issues_sum);
    bucket.scheduled_sum += safeNumber(row.scheduled_sum);
    bucket.demos_sum += safeNumber(row.demos_sum);
    bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
    bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
    bucket.net_amount_sum += safeNumber(row.net_amount_sum);
  });

  return Array.from(buckets.values())
    .map((row = {}) => ({
      ...row,
      client_count: Math.max(
        row.clientCountSet instanceof Set ? row.clientCountSet.size : 0,
        safeNumber(row.precomputedClientCount)
      ),
    }))
    .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
}

function buildTotals(rows = [], showFee = true) {
  return (rows || []).reduce(
    (acc, row = {}) => {
      const baseSpend = safeNumber(row.total_spend_sum);
      const spend = showFee ? safeNumber(row.feeWeightedSpend) : baseSpend;
      acc.clientCount += safeNumber(row.client_count);
      acc.spend += spend;
      acc.leads += safeNumber(row.total_leads_sum);
      acc.selfBook += safeNumber(row.self_books_sum);
      acc.accepted += safeNumber(row.accepted_sum);
      acc.success += safeNumber(row.success_sum);
      acc.outOfArea += safeNumber(row.out_of_areas_count_sum);
      acc.booked += safeNumber(row.booked_sum);
      acc.issues += safeNumber(row.issues_sum);
      acc.scheduled += safeNumber(row.scheduled_sum);
      acc.demos += safeNumber(row.demos_sum);
      acc.grossSold += safeNumber(row.gross_sales_sum);
      acc.netSold += safeNumber(row.net_sales_sum);
      acc.netAmount += safeNumber(row.net_amount_sum);
      return acc;
    },
    {
      clientCount: 0,
      spend: 0,
      leads: 0,
      selfBook: 0,
      accepted: 0,
      success: 0,
      outOfArea: 0,
      booked: 0,
      issues: 0,
      scheduled: 0,
      demos: 0,
      grossSold: 0,
      netSold: 0,
      netAmount: 0,
    }
  );
}

function aggregateSummaryRowsByDay(summaryRows = [], clientMeta = []) {
  const clientFeeById = new Map(
    (clientMeta || []).map((client = {}) => [String(client.value), safeNumber(client.feeRate)])
  );
  const buckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const date = row?.date ? String(row.date) : '';
    if (!date) {
      return;
    }

    if (!buckets.has(date)) {
      buckets.set(date, {
        date,
        total_spend_sum: 0,
        total_leads_sum: 0,
        booked_sum: 0,
        issues_sum: 0,
        demos_sum: 0,
        net_sales_sum: 0,
        spendWithFees: 0,
      });
    }

    const bucket = buckets.get(date);
    const spend = safeNumber(row.total_spend_sum);
    const feeRate = clientFeeById.get(String(row.client_id)) || 0;
    bucket.total_spend_sum += spend;
    bucket.total_leads_sum += safeNumber(row.total_leads_sum);
    bucket.booked_sum += safeNumber(row.booked_sum);
    bucket.issues_sum += safeNumber(row.issues_sum);
    bucket.demos_sum += safeNumber(row.demos_sum);
    bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
    bucket.spendWithFees += spend * (1 + feeRate);
  });

  return Array.from(buckets.values()).sort((left, right) => left.date.localeCompare(right.date));
}

function aggregateSummaryRowsByMonth(summaryRows = [], clientMeta = []) {
  const clientFeeById = new Map(
    (clientMeta || []).map((client = {}) => [String(client.value), safeNumber(client.feeRate)])
  );
  const buckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const rawDate = row?.date ? new Date(row.date) : null;
    if (!(rawDate instanceof Date) || Number.isNaN(rawDate.getTime())) {
      return;
    }
    const key = `${rawDate.getFullYear()}-${String(rawDate.getMonth() + 1).padStart(2, '0')}`;
    const month = rawDate.toLocaleString('en-US', { month: 'short', year: 'numeric' });

    if (!buckets.has(key)) {
      buckets.set(key, {
        id: key,
        month,
        sortKey: key,
        total_spend_sum: 0,
        total_leads_sum: 0,
        self_books_sum: 0,
        accepted_sum: 0,
        success_sum: 0,
        out_of_areas_count_sum: 0,
        booked_sum: 0,
        issues_sum: 0,
        scheduled_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
        feeWeightedSpend: 0,
      });
    }

    const bucket = buckets.get(key);
    const spend = safeNumber(row.total_spend_sum);
    const feeRate = clientFeeById.get(String(row.client_id)) || 0;
    bucket.total_spend_sum += spend;
    bucket.total_leads_sum += safeNumber(row.total_leads_sum);
    bucket.self_books_sum += safeNumber(row.self_books_sum);
    bucket.accepted_sum += safeNumber(row.accepted_sum);
    bucket.success_sum += safeNumber(row.success_sum);
    bucket.out_of_areas_count_sum += safeNumber(row.out_of_areas_count_sum);
    bucket.booked_sum += safeNumber(row.booked_sum);
    bucket.issues_sum += safeNumber(row.issues_sum);
    bucket.scheduled_sum += safeNumber(row.scheduled_sum);
    bucket.demos_sum += safeNumber(row.demos_sum);
    bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
    bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
    bucket.net_amount_sum += safeNumber(row.net_amount_sum);
    bucket.feeWeightedSpend += spend * (1 + feeRate);
  });

  return Array.from(buckets.values()).sort((left, right) => right.sortKey.localeCompare(left.sortKey));
}

const formatAxisCurrency = (value) => {
  const numeric = safeNumber(value);
  if (Math.abs(numeric) >= 1000) {
    return `$${(numeric / 1000).toFixed(0)}k`;
  }
  return `$${numeric.toFixed(0)}`;
};

const chartTooltipFormatter = (value, name) => {
  if (name === 'Spend' || name === 'Spend (incl. fees)') {
    return [new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(value)), name];
  }
  return [safeNumber(value).toLocaleString('en-US'), name];
};

function formatDayLabel(value) {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function createMetricBucket(id, labelKey, label) {
  return {
    id,
    [labelKey]: label,
    feeWeightedSpend: 0,
    clientCountSet: new Set(),
    total_spend_sum: 0,
    total_leads_sum: 0,
    self_books_sum: 0,
    accepted_sum: 0,
    success_sum: 0,
    out_of_areas_count_sum: 0,
    booked_sum: 0,
    issues_sum: 0,
    scheduled_sum: 0,
    demos_sum: 0,
    gross_sales_sum: 0,
    net_sales_sum: 0,
    net_amount_sum: 0,
  };
}

function applySummaryRowToBucket(bucket, row, feeRate = 0) {
  const spend = safeNumber(row.total_spend_sum);
  bucket.total_spend_sum += spend;
  bucket.feeWeightedSpend += spend * (1 + feeRate);
  bucket.total_leads_sum += safeNumber(row.total_leads_sum);
  bucket.self_books_sum += safeNumber(row.self_books_sum);
  bucket.accepted_sum += safeNumber(row.accepted_sum);
  bucket.success_sum += safeNumber(row.success_sum);
  bucket.out_of_areas_count_sum += safeNumber(row.out_of_areas_count_sum);
  bucket.booked_sum += safeNumber(row.booked_sum);
  bucket.issues_sum += safeNumber(row.issues_sum);
  bucket.scheduled_sum += safeNumber(row.scheduled_sum);
  bucket.demos_sum += safeNumber(row.demos_sum);
  bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
  bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
  bucket.net_amount_sum += safeNumber(row.net_amount_sum);
}

function finalizeMetricBucket(bucket, children = []) {
  return {
    ...bucket,
    client_count: bucket.clientCountSet instanceof Set ? bucket.clientCountSet.size : safeNumber(bucket.client_count),
    children,
  };
}

function getClientLabel(row = {}, clientLookup = new Map()) {
  const clientId = row.client_id == null ? '' : String(row.client_id);
  return (
    row.client__name ||
    row.client_name ||
    (clientId ? clientLookup.get(clientId) : '') ||
    'Unknown Client'
  );
}

function buildGeographicHierarchyRows(summaryRows = [], clientMeta = [], selectedClientId = '', parentLabelGetter = () => 'Unknown', parentKey = 'label') {
  const clientFeeById = new Map(
    (clientMeta || []).map((client = {}) => [String(client.value), safeNumber(client.feeRate)])
  );
  const clientNameById = new Map(
    (clientMeta || []).map((client = {}) => [String(client.value), client.label || client.name || ''])
  );
  const parentBuckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }

    const parentLabel = parentLabelGetter(row);
    const verticalLabel = (row.vertical_label || row.vertical || '').trim() || 'Unspecified';
    const clientLabel = getClientLabel(row, clientNameById);
    const feeRate = clientId ? clientFeeById.get(clientId) || 0 : 0;
    const parentId = `${parentKey}:${parentLabel}`;
    const verticalId = `${parentId}:vertical:${verticalLabel}`;
    const clientBucketId = `${verticalId}:client:${clientId || clientLabel}`;

    if (!parentBuckets.has(parentLabel)) {
      parentBuckets.set(parentLabel, {
        bucket: createMetricBucket(parentId, parentKey, parentLabel),
        verticals: new Map(),
      });
    }

    const parentEntry = parentBuckets.get(parentLabel);
    applySummaryRowToBucket(parentEntry.bucket, row, feeRate);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      parentEntry.bucket.clientCountSet.add(clientId);
    }

    if (!parentEntry.verticals.has(verticalLabel)) {
      parentEntry.verticals.set(verticalLabel, {
        bucket: createMetricBucket(verticalId, 'vertical', verticalLabel),
        clients: new Map(),
      });
    }

    const verticalEntry = parentEntry.verticals.get(verticalLabel);
    applySummaryRowToBucket(verticalEntry.bucket, row, feeRate);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      verticalEntry.bucket.clientCountSet.add(clientId);
    }

    if (!verticalEntry.clients.has(clientBucketId)) {
      verticalEntry.clients.set(clientBucketId, {
        ...createMetricBucket(clientBucketId, 'client', clientLabel),
        client_count: safeNumber(row.total_spend_sum) > 0 && clientId ? 1 : 0,
      });
    }

    const clientBucket = verticalEntry.clients.get(clientBucketId);
    applySummaryRowToBucket(clientBucket, row, feeRate);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      clientBucket.clientCountSet.add(clientId);
    }
  });

  return Array.from(parentBuckets.values())
    .map(({ bucket, verticals }) => {
      const verticalRows = Array.from(verticals.values())
        .map(({ bucket: verticalBucket, clients }) => {
          const clientRows = Array.from(clients.values())
            .map((clientBucket) => finalizeMetricBucket(clientBucket))
            .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
          return finalizeMetricBucket(verticalBucket, clientRows);
        })
        .sort((left, right) => right.total_spend_sum - left.total_spend_sum);

      return finalizeMetricBucket(bucket, verticalRows);
    })
    .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
}

function getDisplayMetrics(row = {}, showFee = true) {
  const baseSpend = safeNumber(row.total_spend_sum);
  const spend = showFee ? safeNumber(row.feeWeightedSpend) : baseSpend;
  const leads = safeNumber(row.total_leads_sum);
  const selfBook = safeNumber(row.self_books_sum);
  const accepted = safeNumber(row.accepted_sum);
  const success = safeNumber(row.success_sum);
  const outOfArea = safeNumber(row.out_of_areas_count_sum);
  const booked = safeNumber(row.booked_sum);
  const issues = safeNumber(row.issues_sum);
  const scheduled = safeNumber(row.scheduled_sum);
  const demos = safeNumber(row.demos_sum);
  const grossSold = safeNumber(row.gross_sales_sum);
  const netSold = safeNumber(row.net_sales_sum);
  const netAmount = safeNumber(row.net_amount_sum);

  return {
    spend,
    leads,
    selfBook,
    accepted,
    success,
    outOfArea,
    booked,
    issues,
    scheduled,
    demos,
    grossSold,
    netSold,
    netAmount,
  };
}

function SummaryMetricsCells({ row, showFee, showClientCount, label, hasChildren = false, level = 0, expanded = false, onToggle }) {
  const { spend, leads, selfBook, accepted, success, outOfArea, booked, issues, scheduled, demos, grossSold, netSold, netAmount } =
    getDisplayMetrics(row, showFee);
  const indent = level * 2.5;

  return (
    <>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', pl: indent }}>
          {hasChildren ? (
            <IconButton size="small" onClick={onToggle} sx={{ mr: 0.5 }}>
              {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32, flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: level === 0 ? 700 : 400 }}>
            {label}
          </Typography>
        </Box>
      </TableCell>
      {showClientCount ? <TableCell>{safeNumber(row.client_count)}</TableCell> : null}
      <TableCell><DollarFormatter value={spend} /></TableCell>
      <TableCell>{leads}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
      <TableCell>{selfBook}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(selfBook, leads)} /></TableCell>
      <TableCell>{accepted}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
      <TableCell>{success}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
      <TableCell>{outOfArea}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
      <TableCell>{booked}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
      <TableCell>{issues}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
      <TableCell>{demos}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
      <TableCell>{grossSold}</TableCell>
      <TableCell>{netSold}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
      <TableCell><DollarFormatter value={netAmount} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
    </>
  );
}

function PerformanceSummaryTable({
  title,
  subheader,
  labelHeader,
  labelKey,
  rows,
  totals,
  showFee,
  emptyMessage,
  showClientCount = false,
  loading = false,
}) {
  return (
    <Card>
      <CardHeader title={title} subheader={subheader} />
      <CardContent>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>{labelHeader}</TableCell>
                {showClientCount ? <TableCell>Clients</TableCell> : null}
                <TableCell>{showFee ? 'Spend (incl. fees)' : 'Spend'}</TableCell>
                <TableCell>Leads</TableCell>
                <TableCell>CPL</TableCell>
                <TableCell>Self Book</TableCell>
                <TableCell>CP Self Book</TableCell>
                <TableCell>Self Book Rate</TableCell>
                <TableCell>Accepted</TableCell>
                <TableCell>Accepted Rate</TableCell>
                <TableCell>Success</TableCell>
                <TableCell>Success Rate</TableCell>
                <TableCell>Out of Area</TableCell>
                <TableCell>Out of Area Rate</TableCell>
                <TableCell>Booked</TableCell>
                <TableCell>CP Booked</TableCell>
                <TableCell>Set Rate</TableCell>
                <TableCell>Issues</TableCell>
                <TableCell>CP Issue</TableCell>
                <TableCell>Issue Rate</TableCell>
                <TableCell>Lead To Issue</TableCell>
                <TableCell>Demos</TableCell>
                <TableCell>CP Demo</TableCell>
                <TableCell>Demo Rate</TableCell>
                <TableCell>Gross Sold</TableCell>
                <TableCell>Net Sold</TableCell>
                <TableCell>Net Close Rate</TableCell>
                <TableCell>Net Amount</TableCell>
                <TableCell>Avg Deal</TableCell>
                <TableCell>Revenue per Lead</TableCell>
                <TableCell>Cost of Marketing</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                <>
                  {rows.map((row) => {
                    return (
                      <TableRow key={row.id}>
                        <SummaryMetricsCells
                          row={row}
                          showFee={showFee}
                          showClientCount={showClientCount}
                          label={row[labelKey]}
                        />
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    {showClientCount ? <TableCell sx={{ fontWeight: 700 }}>{totals.clientCount}</TableCell> : null}
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={totals.spend} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.leads}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.selfBook}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.selfBook)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.selfBook, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.accepted}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.accepted, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.success}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.success, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.outOfArea}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.outOfArea, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.booked}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.booked)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.booked, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.issues}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.issues)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.issues, totals.scheduled)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.issues, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.demos}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.demos)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.demos, totals.issues)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.grossSold}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.netSold}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.netSold, totals.demos)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={totals.netAmount} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.netAmount, totals.netSold)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.netAmount, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.spend, totals.netAmount)} /></TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={showClientCount ? 31 : 30} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Loading…' : emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function ExpandablePerformanceSummaryTable({
  title,
  subheader,
  labelHeader,
  labelKey,
  rows,
  totals,
  showFee,
  emptyMessage,
  showClientCount = false,
  loading = false,
}) {
  const [expandedRows, setExpandedRows] = React.useState({});

  React.useEffect(() => {
    setExpandedRows({});
  }, [rows]);

  const toggleRow = React.useCallback((rowId) => {
    setExpandedRows((current) => ({
      ...current,
      [rowId]: !current[rowId],
    }));
  }, []);

  const renderRows = React.useCallback((items = [], level = 0) => (
    items.flatMap((row) => {
      const hasChildren = Array.isArray(row.children) && row.children.length > 0;
      const expanded = Boolean(expandedRows[row.id]);
      const label = row[labelKey] || row.vertical || row.client || row.state || row.dma || 'Unknown';
      const rendered = [
        <TableRow key={row.id}>
          <SummaryMetricsCells
            row={row}
            showFee={showFee}
            showClientCount={showClientCount}
            label={label}
            hasChildren={hasChildren}
            level={level}
            expanded={expanded}
            onToggle={hasChildren ? () => toggleRow(row.id) : undefined}
          />
        </TableRow>,
      ];

      if (hasChildren) {
        rendered.push(
          <TableRow key={`${row.id}:children`}>
            <TableCell sx={{ p: 0, borderBottom: expanded ? 0 : undefined }} colSpan={showClientCount ? 31 : 30}>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Table size="small" sx={compactTableSx}>
                  <TableBody>{renderRows(row.children, level + 1)}</TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        );
      }

      return rendered;
    })
  ), [expandedRows, labelKey, showClientCount, showFee, toggleRow]);

  return (
    <Card>
      <CardHeader title={title} subheader={subheader} />
      <CardContent>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>{labelHeader}</TableCell>
                {showClientCount ? <TableCell>Clients</TableCell> : null}
                <TableCell>{showFee ? 'Spend (incl. fees)' : 'Spend'}</TableCell>
                <TableCell>Leads</TableCell>
                <TableCell>CPL</TableCell>
                <TableCell>Self Book</TableCell>
                <TableCell>CP Self Book</TableCell>
                <TableCell>Self Book Rate</TableCell>
                <TableCell>Accepted</TableCell>
                <TableCell>Accepted Rate</TableCell>
                <TableCell>Success</TableCell>
                <TableCell>Success Rate</TableCell>
                <TableCell>Out of Area</TableCell>
                <TableCell>Out of Area Rate</TableCell>
                <TableCell>Booked</TableCell>
                <TableCell>CP Booked</TableCell>
                <TableCell>Set Rate</TableCell>
                <TableCell>Issues</TableCell>
                <TableCell>CP Issue</TableCell>
                <TableCell>Issue Rate</TableCell>
                <TableCell>Lead To Issue</TableCell>
                <TableCell>Demos</TableCell>
                <TableCell>CP Demo</TableCell>
                <TableCell>Demo Rate</TableCell>
                <TableCell>Gross Sold</TableCell>
                <TableCell>Net Sold</TableCell>
                <TableCell>Net Close Rate</TableCell>
                <TableCell>Net Amount</TableCell>
                <TableCell>Avg Deal</TableCell>
                <TableCell>Revenue per Lead</TableCell>
                <TableCell>Cost of Marketing</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                <>
                  {renderRows(rows)}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    {showClientCount ? <TableCell sx={{ fontWeight: 700 }}>{totals.clientCount}</TableCell> : null}
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={totals.spend} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.leads}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.selfBook}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.selfBook)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.selfBook, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.accepted}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.accepted, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.success}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.success, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.outOfArea}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.outOfArea, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.booked}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.booked)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.booked, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.issues}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.issues)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.issues, totals.scheduled)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.issues, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.demos}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.spend, totals.demos)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.demos, totals.issues)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.grossSold}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{totals.netSold}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.netSold, totals.demos)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={totals.netAmount} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.netAmount, totals.netSold)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><DollarFormatter value={safeDiv(totals.netAmount, totals.leads)} /></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}><PercentageFormatter value={safeDiv(totals.spend, totals.netAmount)} /></TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={showClientCount ? 31 : 30} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Loading…' : emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default function CompanyPerformance() {
  const defaultRange = React.useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = React.useState(defaultRange.start);
  const [endDate, setEndDate] = React.useState(defaultRange.end);
  const [showFee, setShowFee] = React.useState(true);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [selectedVertical, setSelectedVertical] = React.useState('');
  const [dayRange, setDayRange] = React.useState(DAY_RANGE_OPTIONS[1]);
  const [summaryRows, setSummaryRows] = React.useState([]);
  const [channelRowsRaw, setChannelRowsRaw] = React.useState([]);
  const [stateRowsRaw, setStateRowsRaw] = React.useState([]);
  const [dmaRowsRaw, setDmaRowsRaw] = React.useState([]);
  const [timeSeriesRows, setTimeSeriesRows] = React.useState([]);
  const [monthSeriesRows, setMonthSeriesRows] = React.useState([]);
  const [verticalOptions, setVerticalOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [geoLoading, setGeoLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();
  const clients = React.useMemo(() => clientOptions, [clientOptions]);

  React.useEffect(() => {
    let active = true;

    const loadPrimaryData = async () => {
      setLoading(true);
      setError(null);
      try {
        const basePayload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
        };
        const trailingMonthRange = getTrailingMonthRange(endDate, 9);
        const monthPayload = {
          date_after: formatDateToYMD(trailingMonthRange.start),
          date_before: formatDateToYMD(trailingMonthRange.end),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
        };
        const [summaryResponse, channelResponse, timeSeriesResponse, monthSeriesResponse] = await Promise.all([
          fetchVerticalSummary(basePayload),
          fetchChannelSummaryByDate(basePayload),
          fetchClientSummaryByDate(basePayload),
          fetchClientSummaryByDate(monthPayload),
        ]);

        if (!active) {
          return;
        }

        const verticalOptionRows = Array.isArray(summaryResponse?.data) ? summaryResponse.data : [];
        setVerticalOptions(
          Array.from(
            new Set(
              verticalOptionRows
                .map((row = {}) => (row.vertical || '').trim())
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b))
        );
        setSummaryRows(verticalOptionRows);
        setChannelRowsRaw(Array.isArray(channelResponse?.data) ? channelResponse.data : []);
        setTimeSeriesRows(Array.isArray(timeSeriesResponse?.data) ? timeSeriesResponse.data : []);
        setMonthSeriesRows(Array.isArray(monthSeriesResponse?.data) ? monthSeriesResponse.data : []);
      } catch (err) {
        console.error('Failed to load company performance data', err);
        if (active) {
          setError('Failed to load company performance data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPrimaryData();

    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId]);

  React.useEffect(() => {
    let active = true;

    const loadGeographicData = async () => {
      setGeoLoading(true);
      try {
        const basePayload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
        };
        const breakdownPayload = {
          ...basePayload,
          ...(selectedVertical ? { vertical: selectedVertical } : {}),
        };
        const [stateResponse, dmaResponse] = await Promise.all([
          fetchDailyPulseByState(breakdownPayload),
          fetchDailyPulseByDMA(breakdownPayload),
        ]);

        if (!active) {
          return;
        }

        setStateRowsRaw(Array.isArray(stateResponse?.data) ? stateResponse.data : []);
        setDmaRowsRaw(Array.isArray(dmaResponse?.data) ? dmaResponse.data : []);
      } catch (err) {
        console.error('Failed to load geographic performance data', err);
        if (active) {
          setStateRowsRaw([]);
          setDmaRowsRaw([]);
        }
      } finally {
        if (active) {
          setGeoLoading(false);
        }
      }
    };

    loadGeographicData();

    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId, selectedVertical]);

  const allVerticalRows = React.useMemo(
    () => aggregateBreakdownRows(
      summaryRows,
      clients,
      '',
      (row = {}) => (row.vertical || '').trim() || 'Unspecified',
      'vertical'
    ),
    [summaryRows, clients]
  );

  const rows = React.useMemo(
    () => (selectedVertical ? allVerticalRows.filter((row = {}) => row.vertical === selectedVertical) : allVerticalRows),
    [allVerticalRows, selectedVertical]
  );
  const channelRows = React.useMemo(
    () => aggregateBreakdownRows(
      channelRowsRaw,
      clients,
      selectedClientId,
      (row = {}) => (row.channel__name || row.channel || row.channel_name || '').trim() || 'Unspecified',
      'channel'
    ),
    [channelRowsRaw, clients, selectedClientId]
  );
  const stateRows = React.useMemo(
    () => aggregateBreakdownRows(
      stateRowsRaw,
      clients,
      selectedClientId,
      (row = {}) =>
        (row.displayName || row.state || row.state__name || row.state_name || '').trim() || 'Unknown State',
      'state'
    ),
    [clients, selectedClientId, stateRowsRaw]
  );
  const dmaRows = React.useMemo(
    () => aggregateBreakdownRows(
      dmaRowsRaw,
      clients,
      selectedClientId,
      (row = {}) =>
        (
          row.displayName ||
          row.dma_vertical_label ||
          row.dma__name ||
          row.dma ||
          row.dma_name ||
          ''
        ).trim() || 'Unknown DMA',
      'dma'
    ),
    [clients, dmaRowsRaw, selectedClientId]
  );
  const stateHierarchyRows = React.useMemo(
    () => buildGeographicHierarchyRows(
      stateRowsRaw,
      clients,
      selectedClientId,
      (row = {}) =>
        (row.displayName || row.state || row.state__name || row.state_name || '').trim() || 'Unknown State',
      'state'
    ),
    [clients, selectedClientId, stateRowsRaw]
  );
  const dmaHierarchyRows = React.useMemo(
    () => buildGeographicHierarchyRows(
      dmaRowsRaw,
      clients,
      selectedClientId,
      (row = {}) =>
        (
          row.displayName ||
          row.dma_vertical_label ||
          row.dma__name ||
          row.dma_name ||
          row.dma ||
          ''
        ).trim() || 'Unknown DMA',
      'dma'
    ),
    [clients, dmaRowsRaw, selectedClientId]
  );
  const summaryByDay = React.useMemo(
    () => aggregateSummaryRowsByDay(timeSeriesRows, clients),
    [timeSeriesRows, clients]
  );
  const monthRows = React.useMemo(
    () => aggregateSummaryRowsByMonth(monthSeriesRows, clients).slice(0, 9),
    [monthSeriesRows, clients]
  );
  const dodRows = React.useMemo(() => {
    const sorted = [...summaryByDay].sort((left, right) => right.date.localeCompare(left.date));
    return sorted.slice(0, dayRange).map((row = {}) => ({
      ...row,
      id: row.date,
      day: formatDayLabel(row.date),
      feeWeightedSpend: safeNumber(row.spendWithFees),
    }));
  }, [dayRange, summaryByDay]);
  const chartData = React.useMemo(
    () =>
      summaryByDay.map((row = {}) => ({
        ...row,
        spendWithFees: safeNumber(row.spendWithFees),
      })),
    [summaryByDay]
  );

  const totals = React.useMemo(() => buildTotals(rows, showFee), [rows, showFee]);
  const channelTotals = React.useMemo(() => buildTotals(channelRows, showFee), [channelRows, showFee]);
  const monthTotals = React.useMemo(() => buildTotals(monthRows, showFee), [monthRows, showFee]);
  const dodTotals = React.useMemo(() => buildTotals(dodRows, showFee), [dodRows, showFee]);
  const stateTotals = React.useMemo(() => buildTotals(stateRows, showFee), [stateRows, showFee]);
  const dmaTotals = React.useMemo(() => buildTotals(dmaRows, showFee), [dmaRows, showFee]);

  const handleDateRangeChange = React.useCallback((ranges) => {
    const nextStart = ranges?.selection?.startDate;
    const nextEnd = ranges?.selection?.endDate;
    if (nextStart instanceof Date && nextEnd instanceof Date) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    }
  }, []);

  return (
    <Page title="Company Performance" fullWidth isLoading={loading}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">Company Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Staff-only vertical summary rollup using the same metrics as the client dashboard vertical table.
          </Typography>
        </Box>

        <Paper
          sx={{
            ...stickyControlsSx,
            backgroundColor: 'background.paper',
            p: { xs: 1.25, md: 1.5 },
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  style={{ width: '100%' }}
                />
              </Box>
              <Autocomplete
                sx={{ minWidth: 260 }}
                options={clientOptions}
                loading={clientsLoading}
                value={findClientOptionByValue(selectedClientId)}
                onInputChange={handleClientInputChange}
                onChange={(_event, option) => setSelectedClientId(option ? String(option.value) : '')}
                isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="All clients"
                  />
                )}
              />
              <Autocomplete
                sx={{ minWidth: 260 }}
                options={verticalOptions}
                value={selectedVertical || null}
                onChange={(_event, option) => setSelectedVertical(option || '')}
                getOptionLabel={(option) => option || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vertical Filter"
                    placeholder="All verticals"
                  />
                )}
              />
            </Stack>
            <FormControlLabel
              control={<Switch checked={showFee} onChange={(event) => setShowFee(event.target.checked)} />}
              label="Include fees"
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardHeader
            title="Performance Over Time"
            subheader="Spend, leads, booked, issues and demos by day across all matching clients."
          />
          <CardContent>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={showFee ? chartData : summaryByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" tickFormatter={formatAxisCurrency} />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip formatter={chartTooltipFormatter} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey={showFee ? 'spendWithFees' : 'total_spend_sum'}
                    name={showFee ? 'Spend (incl. fees)' : 'Spend'}
                    stroke="#8884d8"
                    yAxisId="left"
                  />
                  <Line type="monotone" dataKey="total_leads_sum" name="Leads" stroke="#82ca9d" yAxisId="right" />
                  <Line type="monotone" dataKey="booked_sum" name="Booked" stroke="#ffc658" yAxisId="right" />
                  <Line type="monotone" dataKey="issues_sum" name="Issues" stroke="#ff7300" yAxisId="right" />
                  <Line type="monotone" dataKey="demos_sum" name="Demos" stroke="#413ea0" yAxisId="right" />
                  <Line type="monotone" dataKey="net_sales_sum" name="Net Sold" stroke="#00C49F" yAxisId="right" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        <PerformanceSummaryTable
          title="MoM Performance Table"
          subheader={`Last ${monthRows.length} month${monthRows.length === 1 ? '' : 's'} of data`}
          labelHeader="Month"
          labelKey="month"
          rows={monthRows}
          totals={monthTotals}
          showFee={showFee}
          emptyMessage="No month-over-month rows found for the last 9 months."
        />

        <Card>
          <CardHeader
            title="DoD Performance Table"
            subheader={`${dodRows.length} day${dodRows.length === 1 ? '' : 's'} shown`}
            action={(
              <ToggleButtonGroup
                color="primary"
                value={dayRange}
                exclusive
                size="small"
                onChange={(_event, nextValue) => {
                  if (nextValue) {
                    setDayRange(nextValue);
                  }
                }}
              >
                {DAY_RANGE_OPTIONS.map((option) => (
                  <ToggleButton key={option} value={option}>
                    {option}d
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
          <CardContent sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Daily performance window similar to the client dashboard.
            </Typography>
          </CardContent>
        </Card>

        <PerformanceSummaryTable
          title="DoD Performance Details"
          subheader="Recent daily performance rows."
          labelHeader="Day"
          labelKey="day"
          rows={dodRows}
          totals={dodTotals}
          showFee={showFee}
          emptyMessage="No day-over-day rows found for the selected range."
        />

        <PerformanceSummaryTable
          title="Vertical Performance Table"
          subheader={`${rows.length} vertical${rows.length === 1 ? '' : 's'} in range`}
          labelHeader="Vertical"
          labelKey="vertical"
          rows={rows}
          totals={totals}
          showFee={showFee}
          emptyMessage="No vertical summary rows found for the selected range."
          showClientCount
        />

        <PerformanceSummaryTable
          title="Channel Performance Table"
          subheader={`${channelRows.length} channel${channelRows.length === 1 ? '' : 's'} in range`}
          labelHeader="Channel"
          labelKey="channel"
          rows={channelRows}
          totals={channelTotals}
          showFee={showFee}
          emptyMessage="No channel summary rows found for the selected range."
        />

        <ExpandablePerformanceSummaryTable
          title="State Performance Table"
          subheader={`${stateRows.length} state${stateRows.length === 1 ? '' : 's'} in range with vertical and client drilldown`}
          labelHeader="State"
          labelKey="state"
          rows={stateHierarchyRows}
          totals={stateTotals}
          showFee={showFee}
          emptyMessage="No state performance rows found for the selected range."
          showClientCount
          loading={geoLoading}
        />

        <ExpandablePerformanceSummaryTable
          title="DMA Performance Table"
          subheader={`${dmaRows.length} DMA${dmaRows.length === 1 ? '' : 's'} in range with vertical and client drilldown`}
          labelHeader="DMA"
          labelKey="dma"
          rows={dmaHierarchyRows}
          totals={dmaTotals}
          showFee={showFee}
          emptyMessage="No DMA performance rows found for the selected range."
          showClientCount
          loading={geoLoading}
        />
      </Stack>
    </Page>
  );
}
